import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import Dropdown from '@/components/global/Dropdown';
import MultiDropdown from '@/components/global/MultiDropdown/MultiDropdown';
import { applyFilters } from '@/lib/filters';

import SearchBox from '../../product/SearchBox';
import { TYPEFACES_FILTERING_CATEGORIES } from '../helpers/typefaceCollectionHelpers';
import styles from './TypefaceHeader.module.scss';

export default function TypefaceHeader({
  attributes,
  searchState,
  isComparing,
  categoryId,
  childCategories,
  filters,
  setFilters,
  sort,
  setSort
}) {
  const [search, setSearch] = searchState;
  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedFiltersIds, setSelectedFiltersIds] = useState<string[]>([]);
  const router = useRouter();
  const params =
    typeof window !== 'undefined' ? new URLSearchParams(window?.location.search) : null;
  const sortParams = params?.get('sort');
  const languagesParams = params?.get('languages');
  const classificationsParams = params?.get('classifications');
  const searchParams = params?.get('search');

  const TYPEFACES_MULTI_MENU_ITEMS = useMemo(
    () => [
      {
        title: 'Languages',
        noneButtonTitle: 'None',
        allButtonTitle: 'All',
        component: 'menuItem',
        items: languageOptions
      },
      {
        title: 'Classification',
        noneButtonTitle: 'None',
        allButtonTitle: 'All',
        component: 'menuItem',
        items: [
          ...childCategories.map((category) => {
            return {
              id: category.node.id,
              name: category.node.name
            };
          })
        ]
      }
    ],
    [childCategories, languageOptions]
  );

  const setMenuFilters = (filter, action?) => {
    let _selectedFilters = [];

    if (Array.isArray(filter)) {
      const _filterIds = filter.map((e) => e.id);

      // add method
      if (action === 'add') {
        _selectedFilters = selectedFilters.filter((f) => !_filterIds.includes(f.id));
        _selectedFilters.push(...filter);
      }

      // remove method
      if (action === 'remove') {
        _selectedFilters = selectedFilters.filter((f) => !_filterIds.includes(f.id));
      }
    } else {
      _selectedFilters = selectedFilters.filter((e) => e.id !== filter.id);
      if (!selectedFiltersIds.includes(filter.id)) _selectedFilters.push(filter);
    }

    const _selectedFiltersIds = _selectedFilters.map((e) => e.id);
    setSelectedFilters(_selectedFilters);
    setSelectedFiltersIds(_selectedFiltersIds);
    // getFilteredData(_selectedFiltersIds);

    // Check if the filter is attribute or category
    const attributeFilters = _selectedFiltersIds.filter((id) => !id.endsWith('='));
    const categoryFilters = _selectedFiltersIds.filter((id) => id.endsWith('='));

    router.push(
      {
        pathname: '/typefaces',
        query: {
          ...(sortParams && { sort: sortParams }),
          ...(attributeFilters.length > 0 && {
            languages: [...attributeFilters].join(',')
          }),
          ...(categoryFilters.length > 0 && {
            classifications: [...categoryFilters].join(',')
          }),
          ...(searchParams && { search: searchParams })
        }
      },
      undefined,
      { shallow: true }
    );
  };

  useEffect(() => {
    attributes.map((attribute) => {
      const attributeOptions = [
        ...attribute?.node?.choices?.edges.map((attributeOption) => {
          return {
            name: attributeOption.node.name,
            id: attributeOption.node.slug
          };
        })
      ];
      setLanguageOptions(attributeOptions);
      return attribute;
    });
  }, [attributes]);

  useEffect(() => {
    if (params) {
      setFilters({
        ...filters,
        attributes: languagesParams
          ? [{ slug: attributes?.[0]?.node?.slug, values: languagesParams.split(',') }]
          : null,
        categories: classificationsParams ? classificationsParams.split(',') : [categoryId],
        ...(searchParams && { search: searchParams })
      });

      if (sortParams) {
        setSort(
          TYPEFACES_FILTERING_CATEGORIES?.find((item) => {
            const sortParamsArray = sortParams.split(':').map((param) => param.toUpperCase());
            return (
              sortParamsArray[0] === item.value.field && sortParamsArray[1] === item.value.direction
            );
          })?.value
        );
      }

      if (searchParams) {
        setSearch(searchParams);
      }
    }
  }, [router.asPath]);

  useEffect(() => {
    setSelectedFilters([
      ...(languagesParams
        ? TYPEFACES_MULTI_MENU_ITEMS[0].items.filter((item) =>
            languagesParams.split(',').includes(item.id)
          )
        : []),
      ...(classificationsParams
        ? TYPEFACES_MULTI_MENU_ITEMS[1].items.filter((item) =>
            classificationsParams.split(',').includes(item.id)
          )
        : [])
    ]);
    setSelectedFiltersIds([
      ...(languagesParams ? languagesParams.split(',') : []),
      ...(classificationsParams ? classificationsParams.split(',') : [])
    ]);
  }, [languagesParams, classificationsParams, TYPEFACES_MULTI_MENU_ITEMS]);

  return (
    <div className={styles.TypefaceHeader}>
      <div className={styles.bar}>
        <div className={styles.filtering}>
          <Dropdown
            items={TYPEFACES_FILTERING_CATEGORIES}
            defaultValue={TYPEFACES_FILTERING_CATEGORIES.find(
              (category) => category.value === sort
            )}
            onSelect={(e) => {
              setSort(e.value);
              router.push(
                {
                  pathname: '/typefaces',
                  query: {
                    sort: `${e.value?.field?.toLowerCase()}:${e.value?.direction?.toLowerCase()}`,
                    ...(languagesParams && { languages: languagesParams }),
                    ...(classificationsParams && { classifications: classificationsParams }),
                    ...(searchParams && { search: searchParams })
                  }
                },
                undefined,
                { shallow: true }
              );
              return e;
            }}
            size="small"
            align="left"
            fullWidth
          />
        </div>
        <div>
          <MultiDropdown
            items={TYPEFACES_MULTI_MENU_ITEMS}
            title="Filter"
            onSelect={(e) => {
              setMenuFilters(e);
              return e;
            }}
            size="small"
            align="left"
            selectedFiltersIds={selectedFiltersIds}
            setMenuFilters={setMenuFilters}
            fullWidth
          />
        </div>
        <div className={styles.searchCont}>
          <SearchBox
            state={searchState}
            disabled={isComparing}
            onSearch={({ value }) => {
              applyFilters({ filters, type: 'search', value, callback: setFilters });
              router.push(
                {
                  pathname: '/typefaces',
                  query: {
                    ...(sortParams && { sort: sortParams }),
                    ...(languagesParams && { languages: languagesParams }),
                    ...(classificationsParams && { classifications: classificationsParams }),
                    search: value
                  }
                },
                undefined,
                { shallow: true }
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
